<script lang="ts">
	import type { NodeData } from '../types/flow-types';
	import { flowEditorState } from '../../../stores/flow_editor';
	import { resolveNoteTheme } from './noteThemes';

	export let id: string;
	export let data: NodeData;
	export let selected: boolean = false;

	$: liveData = $flowEditorState.nodes.find((node) => node.id === id)?.data ?? data;
	$: theme = resolveNoteTheme(liveData);
	$: noteText = String(liveData.noteText ?? '').trim();
</script>

<div
	class="note-node"
	class:selected
	data-testid="flow-note-node"
	style="--note-background: {theme.background}; --note-header: {theme.header}; --note-text: {theme.text}; --note-muted-text: {theme.mutedText}; --note-border: {theme.border}; --note-accent: {theme.accent};"
>
	<div class="note-header" title="Drag to move note">
		<i class="fa fa-note-sticky" aria-hidden="true"></i>
		<span>Note</span>
	</div>
	<div class="note-body" class:empty={!noteText} title={noteText || undefined}>
		{noteText || 'Click to add a note…'}
	</div>
</div>

<style>
	.note-node {
		position: relative;
		min-width: 200px;
		max-width: 320px;
		padding: 0;
		overflow: hidden;
		border: 1px solid var(--note-border);
		border-radius: 4px 4px 12px 4px;
		background: var(--note-background);
		box-shadow:
			0 10px 20px rgba(0, 0, 0, 0.22),
			0 2px 4px rgba(0, 0, 0, 0.14);
		color: var(--note-text);
		box-sizing: border-box;
		font-family: inherit;
	}

	.note-node::after {
		content: '';
		position: absolute;
		right: 0;
		bottom: 0;
		width: 18px;
		height: 18px;
		background: linear-gradient(
			135deg,
			color-mix(in srgb, var(--note-border) 35%, white) 50%,
			rgba(255, 255, 255, 0.42) 50%
		);
		border-top: 1px solid color-mix(in srgb, var(--note-border) 70%, transparent);
		border-left: 1px solid color-mix(in srgb, var(--note-border) 70%, transparent);
		box-shadow: -2px -2px 3px rgba(0, 0, 0, 0.08);
		pointer-events: none;
	}

	.note-node.selected {
		border-color: var(--note-accent);
		box-shadow:
			0 0 0 3px color-mix(in srgb, var(--note-accent) 45%, transparent),
			0 10px 20px rgba(0, 0, 0, 0.22);
	}

	.note-header {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 8px 10px;
		font-size: 0.72rem;
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		cursor: grab;
		user-select: none;
		color: var(--note-text);
		background: var(--note-header);
		border-bottom: 1px solid color-mix(in srgb, var(--note-border) 65%, transparent);
	}

	.note-header:active {
		cursor: grabbing;
	}

	.note-body {
		min-height: 104px;
		max-height: 220px;
		overflow: hidden;
		padding: 12px 14px 20px;
		font-size: 0.86rem;
		font-weight: 520;
		line-height: 1.5;
		white-space: pre-wrap;
		word-break: break-word;
		box-sizing: border-box;
	}

	.note-body.empty {
		color: var(--note-muted-text);
		font-style: italic;
		opacity: 0.78;
	}
</style>
